// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Close from "@hardwoods/XDL/Icons/Close";
import { xBaseTheme } from "@hardwoods/XDL/types";

import { color } from "@hardwoods/XDL/Utilities";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { styledScrollbar } from "@hardwoods/XDL/Utilities/SharedStyles";
import { ButtonIcon } from "@insite/mobius/Button";
import ModalPresentationPropsDefault from "@insite/mobius/Modal/presentationProps";
import { PaginationStyle } from "@insite/mobius/Pagination";
import { TextAreaStyle } from "@insite/mobius/TextArea";
import { css } from "styled-components";

export const modalFooterStyle = css`
    padding: 20px 16px;
    display: flex;
    justify-content: flex-start;
    flex-direction: row-reverse;
    ${xScreen.lte("xs")} {
        flex-direction: column-reverse;
    }

    > * {
        margin-right: 20px;
        margin-left: 0;

        &:first-child {
            margin-right: 0;
        }

        ${xScreen.lte("xs")} {
            width: 100%;
            margin-right: 0;
            margin-bottom: 20px;

            &:last-child {
                margin-right: 0;
            }

            &:first-child {
                margin-bottom: 0;
            }
        }
    }
`;

// XNG-Changes: add "xLarge" as new option in sizeVarient
export const modalBodySelector = `& > *:not([data-test-selector="modalCloseButton"]):not([class^="TextAreaStyle"]):not([class^="PaginationStyle"])`;
export default {
    sizeVariants: {
        small: 500,
        medium: 800,
        large: 1110,
        xLarge: 2000,
    },
    defaultProps: {
        ...ModalPresentationPropsDefault,
        cssOverrides: {
            modalBody: css`
                border: none;
                border-radius: 6px;
                box-shadow: 0 2px 15px 0 ${color("common.black", 0.5)};
                overflow: visible;
                ${xScreen.gt(0)} {
                    height: auto;
                    max-height: calc(100% - 90px);
                }
                ${xScreen.lte("md")} {
                    max-width: calc(100% - 40px);
                }
            `,
            modalTitle: css`
                border: 0;
                padding: 16px 0 10px 0;
                margin: 16px 16px 0 16px;
                border-bottom: 1px solid ${color("common.border")};

                > * {
                    font-size: 20px;
                    font-weight: bold;
                    letter-spacing: 0;
                    line-height: 24px;
                }
            `,
            modalContent: css`
                margin-right: 1px;
                ${styledScrollbar()}
                overflow-y: auto;

                font-size: 15px;
                letter-spacing: 0;
                line-height: 20px;
                padding: 0;

                ${modalBodySelector} {
                    padding: 16px;
                    overflow: hidden;
                    ${xScreen.lte("xs")} {
                        flex-direction: column-reverse;
                        & > button {
                            width: 100%;
                            &:last-child {
                                margin-bottom: 8px;
                            }
                        }
                    }
                }

                .modal-footer {
                    ${modalFooterStyle}
                }
            `,
        },
        closeButtonProps: {
            buttonType: "outline",
            css: css`
                &&& {
                    position: absolute;
                    min-height: 0;
                    top: -18px;
                    right: -18px;
                    width: 36px;
                    height: 36px;
                    padding: 3px;
                    background: ${color("danger")};
                    border: 3px solid ${color("text.inverse")};
                    box-shadow: 0 0 4px 0 ${color("common.black", 0.5)};
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: opacity 300ms ease-in;

                    &:hover {
                        opacity: 0.6;
                        background: ${color("danger")};
                    }
                    ${ButtonIcon} {
                        position: static;
                        color: ${color("text.inverse")};
                    }
                }
            `,
        },
        closeButtonIconProps: {
            src: Close,
            size: 13,
        },
    },
} as xBaseTheme["modal"];
